<template>
  <div id="accessories-page" v-if="webstoreContent">
    <ProductSubNav
      :globals="webstoreContent.webstoreBody"
      :isSticky="productNavIsSticky"
    />
    <transition name="fade">
      <div class="row section-margin" id="product-content-container">
        <div class="md:w-5/12" id="product-image">
          <img
            :alt="activeProductInfo.name"
            :src="activeOffer.offerImage"
          />
        </div>
        <div class="roboto md:w-6/12" id="product-info">
          <div class="info-wrapper">

            <!-- Info -->
            <div>
              <h3 id="product-name" class="mb-0">{{ activeProductInfo.name }}</h3>
              <Pricing v-if="shouldCustomerSeePricing" :accessories="true"/>  
            </div>

            <!-- Contract info -->
            <div v-if="!hasContractExpired && showContractDisclaimer && hasLicenseOffer" class="mb-0 mt-1 text-lg bold leading-tight">
              <span>
                {{ contractDisclaimer }}
              </span>
            </div>

            <div v-if="activeProductInfo.info" class="section-margin-2">
              <p class="text-xl font-normal text-blue-dark" :class="activeOffer.productStatus === 'Out of stock' && 'out-of-stock'">{{ activeProductInfo.info }}</p>
            </div>

            <div v-if="activeProductInfo.compatibleWith && activeProductInfo.compatibleWith.length > 0" class="section-margin-2">
              <div class="product-body-section-headers">{{webstoreContent.accessoriesPage.sectionTitles.compatibility}}</div>
              <ul
                id="offer-included-list"
                :key="bullet"
                v-for="bullet in activeProductInfo.compatibleWith"
              >
                <li
                  class="cursor-pointer list-disc text-lg"
                  @click="navigateToProduct(bullet.productKey, bullet.productFamily)"
                >
                  {{ bullet.productName }}
                </li>
              </ul>
            </div>

            <!-- Good to know -->
            <div class="section-margin-2 !mb-4" v-if="activeProductInfo.goodToKnow">
              <div class="product-body-section-headers"> {{webstoreContent.accessoriesPage.sectionTitles.goodToKnow}}</div>
              <div class="text-lg">
                <div>{{activeProductInfo.goodToKnow}}</div>
              </div>
            </div>

            <!-- Install Guide -->
            <div class="section-margin-2 !mb-4" v-if="activeProductInfo.installationGuide">
              <div class="text-lg">
                <a class="" href="">Download install guide</a>
              </div>
            </div>

            <!-- Specs -->
            <div v-if="!cablePage">
              <div v-if="activeProductInfo.accessorySpecs && activeProductInfo.accessorySpecs.length > 0" class="specs-section">
                <div class="cursor-pointer" v-on:click="toggleSpecs" id="product-body-specs-header">
                  <div>{{ webstoreContent.accessoriesPage.sectionTitles.specs }}</div>
                  <div class="cursor-pointer mb-0" id="plus-button">
                    {{ specsClicked ? '-' : '+' }}
                  </div>
                </div>
                <SlideUpDown class="accordion mt-0" :active="specsClicked" >
                  <div class="accordion-content">
                    <ul class="list-disc ml-1 mt-2">
                      <li
                        v-for="(spec, index) in activeProductInfo.accessorySpecs"
                        :key="spec"
                        class="text-xl dark-blue-text" 
                        :class="index !== activeProductInfo.accessorySpecs.length - 1 && 'mb-1/2'"
                      >
                        {{ spec }}
                      </li>
                    </ul>
                  </div>
                </SlideUpDown>
              </div>
              
              <div :class="activeOffer.productStatus === 'Out of stock' && 'grayed'">
                <!-- License Selection  -->
                <template v-if="anonymousLargeFleet || (isENT && activeOffer && !activeOffer.monthlyUnitPrice)">
                  <div>
                    <vButton
                      position="center"
                      mobile="12"
                      tablet="12"
                      desktop="12"
                      styling="primary rounded"
                      @onClickEvent="anonymousLargeFleet ? $emit('anonymous-inquiry'): inquireModalIsOpen = true"
                      class="add-to-bag"
                    >
                      <template v-slot:content>
                        Inquire
                      </template> 
                    </vButton>
                  </div>
                </template>
                <template v-else-if="(Object.keys(activeProductInfo.offerings).length > 1 && (!isENT || (isENT && activeOffer && activeOffer.monthlyUnitPrice)))">
                  <div>
                    <h3 class="bold text-blue-dark md:mt-4">
                      {{ ['ACC-PR', 'ACC-PB', 'ACC-AUX', 'ACC-CIG-LIGHT'].includes(activeProductKey) ? 'Are you looking for B-class or C-class?' : webstoreContent.cartPage.choose_license }}
                    </h3>
                    <a
                      v-if="['ACC-PR', 'ACC-PB', 'ACC-AUX', 'ACC-CIG-LIGHT'].includes(activeProductKey)"
                      class="mt-0 mb-0"
                      href="https://downloads.ctfassets.net/bx9krvy0u3sx/3HmMci5BzpI9nrLsOtJWxg/68e689e65c3132fcf2b9126e9eba0b7b/cable_guide.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >Unsure? See instructions to identify the class</a>
                    <LicenseSelection :accessories="true" @scroll-to="scrollTo"/>
                  </div>
                </template>

                <!-- Quantity  -->
                <div ref="quantity">
                  <div v-if="shouldCustomerSeePricing && (activeOfferKey || Object.keys(activeProductInfo.offerings).length === 1)">
                    <h3 class="bold text-blue-dark mt-4 mb-0">
                      {{ webstoreContent.cartPage.quantityCopy }}
                    </h3>
                    <div class="quantity-section"
                      :key="offer"
                      v-for="(offer_info, offer) in this.activeProductInfo.offerings"
                    >
                      <ProductQuantity
                        v-if="(Object.keys(activeProductInfo.offerings).length > 1) ? (activeOfferKey && offer_info.offerKey === activeOfferKey) : true"
                        :ref="cleanOfferKey(offer_info.offerKey)"
                        type="product"
                        :showImage="false"
                        :updateTotal="true"
                        :imageSource="offer_info.offerImage"
                        :productName="activeProductInfo.name"
                        :skuText="offer_info.productCodes.join(', ')"
                        :salesPrice="activeProductInfo.offerings[offer].salesPrice"
                        :defaultTerm="activeOffer.defaultPaymentTerm"
                        :cablesRequired="doesOfferRequireCable(activeOfferKey)"
                        :currency="currencySymbol"
                        :maxPurchasableQuantity="offer_info.maxPurchasableQuantity"
                        :minPurchasableQuantity="1"
                        :startingNumber="setInitialOfferQuantity(offer_info)"
                        :value="`${offer} Quantity`"
                        :maxError="webstoreContent.cartPage.max_quantity_error"
                      ></ProductQuantity>
                    </div>
                    <vButton
                        position="center"
                        mobile="12"
                        tablet="12"
                        desktop="12"
                        styling="primary rounded"
                        @onClickEvent="cartUpdateHandler"
                        class="add-to-bag"
                      >
                      <template v-slot:content>
                        Add To Bag
                      </template> 
                    </vButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="cablePage" class="mb-5">
            <!-- cable class selection -->
            <CableQuestion
              :webstoreContent="webstoreContent"
              :selectedTypeOfReplacementCable="selectedTypeOfReplacementCable"
              :isVGCable="isVGCable"
              @set-selected-cable-type="setTypeOfReplacementCable"
            />
            <!-- cable selection logic -->
            <div v-if="!onlyOneCableSelectionMethod" class="section-margin-2 product-body-section-headers cable-selection">
              {{webstoreContent.accessoriesPage.sectionTitles.cableSelectionMethod}}
            </div>
            <CableSelection
              ref="cable-selection"
              :correspondingProduct="getProduct()"
              :isCableAccessoriesPage="true"
              :activeCableSelectionMethods="activeCableSelectionMethods"
              :addToCart="false"
              :selectedTypeOfReplacementCable="selectedTypeOfReplacementCable"
              @scroll-to="scrollToCableSelect"
              @add-selection-method="updateActiveCableSelectionMethods"
              @remove-selection-method="removeActiveCableMethod"
            />
          </div>
        </div>
      </div>
    </transition>
      <!-- more accessories carousel -->
      <div v-if="activeProductInfo.frequentlyBoughtTogether && activeProductInfo.frequentlyBoughtTogether.length > 0" class="md:mt-5 col xs-12 sm-12 lg-12 roboto accessories-carousel">
        <div id="accessories-carousel-messaging">
          <div id="accessories-carousel-header">
            {{webstoreContent.accessoriesPage.sectionTitles.moreAccessoriesMessaging}}
          </div>
          <div>
            {{webstoreContent.accessoriesPage.moreAccessoriesMessaging}}
          </div>
        </div>
        <FrequentlyBoughtTogether :isAccessories="true" :activeProductInfo="activeProductInfo" />
      </div>
      <div v-else class="mb-5"></div>
    <ActionBottomNav
      ref="bottom-nav"
      :submitButtonText="submitButtonText"
      :activeCableSelectionMethods="activeCableSelectionMethods"
      :bottomNavIsShown="bottomNavIsShown"
      :isCableAccessoriesPage="true"
      @scroll-to="scrollTo"
      @add-selection-method="updateActiveCableSelectionMethods"
      @add-to-cart="addToCartCables"
      @anonymous-inquiry="$emit('anonymous-inquiry')"
    />
    <InquireModal
      :isOpen="inquireModalIsOpen"
      @hide-modal="inquireModalIsOpen = false"
    />
  </div>
</template>

<script>

import ActionBottomNav from './components/ActionBottomNav';
import FrequentlyBoughtTogether from "../webstore/components/FrequentlyBoughtTogether";
import CableSelection from "../../../components/cable-selection/cable-selection"
import ProductSubNav from "../webstore/components/ProductSubNav.vue";
import SlideUpDown from 'vue-slide-up-down';
import ProductQuantity from "../webstore/product-quantity";
import vButton from "../../utils/button.vue";
import Pricing from "./components/Pricing";
import LicenseSelection from "./components/LicenseSelection";
import product from '../../../store/modules/product/index';
import CableQuestion from "../webstore/components/CableQuestion"
import InquireModal from './inquire-modal';
import ReplacementCableQuestion from "../../../mixins/replacement-cable-question";
import { UpdateShoppingCartHandlers } from "../../../mixins/update-shopping-cart-handlers";
import { WebstoreResources } from "../../../mixins/webstore-resources";
export default {
  mixins: [ UpdateShoppingCartHandlers, WebstoreResources, ReplacementCableQuestion ],

  components: {
    FrequentlyBoughtTogether,
    ProductSubNav,
    SlideUpDown,
    ProductQuantity,
    vButton,
    CableSelection,
    Pricing,
    LicenseSelection,
    ActionBottomNav,
    CableQuestion,
    InquireModal,
  },
  data() {
    return {
      specsClicked: false,
      useHidden: true,
      productNavIsSticky: false,
      activeCableSelectionMethods: [],
      bottomNavIsShown: false,
      cablesSelected: [],
      selectedTypeOfReplacementCable: '',
      inquireModalIsOpen: false,
      anonymousInquireModal: false
    };
  },
  computed: {
    cablePage() {
      return this.activeProductKey.includes("CBL") && this.activeProductKey !== 'CBL-VG-CAUX';
    },
    isVGCable() {
      return this.activeProductKey === 'VG-CBL'; 
    },
    hasLicenseOffer() {
      let lic_found = false;
      Object.keys(this.activeProductInfo.offerings).forEach(offer => {
        if (offer.includes("License")) {
          lic_found = true;
        }
      })
      return lic_found
    }
  },
  methods: {

    toggleSpecs() {
      this.specsClicked = !this.specsClicked
    },

    cartUpdateHandler() {
      this.updateCart();
    },

    getProduct() {
      return this.activeProductKey.split("-")[0]
    },

    updateActiveCableSelectionMethods(method, reset) {
      this.bottomNavIsShown = true;
      if (reset) {
        this.activeCableSelectionMethods = [method]
      } else if (!this.activeCableSelectionMethods.includes(method)) {
        this.activeCableSelectionMethods.push(method);
      }
    },

    removeActiveCableMethod(method) {
      if (this.activeCableSelectionMethods.includes(method)) {
        this.activeCableSelectionMethods.splice(this.activeCableSelectionMethods.indexOf(method), 1);
      }
    },

    handleScroll() {
      this.productNavIsSticky = (window.scrollY > 81);
    },
    
    scrollToCableSelect() {
      var top = this.$refs['cable-selection'].$refs["cableSelect"].offsetHeight + 10000000000

      window.scrollTo({
        left: 0,
        top,
        behavior: 'smooth',
      });
    },

    setTypeOfReplacementCable(value) {
      window.scrollTo({
        left: 0,
        top: 500,
        behavior: 'smooth',
      });
      this.selectedTypeOfReplacementCable = value;
    },

    scrollTo() {
      var top = this.$refs['quantity'].offsetTop;

      // TODO: make the scroll go further down when first selecting the license
      if (this.activeOfferKey || Object.keys(this.activeProductInfo.offerings).length === 1) {
        top = top - 130;
      } else {
        top = top + 500
      }

      window.scrollTo({
        left: 0,
        top,
        behavior: 'smooth',
      });
    },

    cableProductsInProductCatalog(){
      let typeOfProductCable = this.typeOfProductCable();
      if (
        "CBL" in this.productCatalog &&
        typeOfProductCable in this.productCatalog.CBL
      ) {
        let cablesAvailable = this.productCatalog.CBL[typeOfProductCable]
          .offerings;
        if(cablesAvailable){
          return cablesAvailable;
        }
      }
    },

    typeOfProductCable() {
      return this.activeProductKey.includes("AG")
        ? "AG-CBL"
        : "VG-CBL";
    },

    populateCablesSelected() {
      //iterating through the totalCompiledData from Selection Info to create an array of offers
      let cableProducts = this.cableProductsInProductCatalog();
      for(let cableInfo of this.totalCablesSubmittedData){
          // Handle local cable selection error (no real offer for SVC)
          if (cableInfo.cable === "Samsara Vehicle Cable") {
            cableInfo.cable = "CBL-AG-A7WY"
          }
          let submittedInfo = {
            "offer": cableProducts[cableInfo.cable],
            "quantity": cableInfo.quantity
          }
          this.cablesSelected.push(submittedInfo);
      }
    },

    navigateToProduct(key, family) {
      this.setActiveProductKey(key);
      this.setActiveProductFamily(family);
      this.setActiveProductTabDefault();
    },

    addToCartCables() {
      this.populateCablesSelected();
      this.updateCart();
      this.setActiveProductFamilyInactive();
      this.setActiveProductKeyInactive();
      this.setActiveOfferKeyInactive();
      this.setShoppingCartToOpened();
    },
  },

  mounted() {
      window.addEventListener('scroll', this.handleScroll);
      this.selectedTypeOfReplacementCable = ''
      setTimeout(() => {
        if (this.cablePage) {
          this.setActiveOfferKey("Cable");
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/vin/clearVinData`
          );
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/manualSelection/clearManualSelectionData`
          );
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/mmy/clearMMYData`
          );
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/vin/clearSubmittedVinData`
          );
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/mmy/clearSubmittedMMYData`
          );
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/base/setCablesSelectedCount`
          );
          this.$store.commit(
            `${this.activeProductKey}${this.activeOfferKey}CableSelection/base/setTotalCablesSubmittedData`
          );
        }
        if (!this.$store.getters[`${this.productKeyOverride ? this.productKeyOverride : this.activeProductKey}/quantity`]) {
          this.$store.registerModule(`${this.productKeyOverride ? this.productKeyOverride : this.activeProductKey}`, product)
        }
      }, 0)

    setTimeout(() => this.$emit('stop-loader'), 1000);
      
  }
};
</script>

<style lang="scss" scoped>
.roboto {
  font-family: "Roboto", Helvetica, sans-serif;
}

.out-of-stock {
  color: #EE6917;
}

#accessories-page {
  font-family: "Roboto", sans-serif;
  font-style: normal;
  font-weight: normal;
  -webkit-animation: fadeIn 1s;
  animation: fadeIn 1s;
  white-space: initial !important;
}

#accessories-page a {
  color: #0384FB;
  font-size: 18px;
  margin-top: 10px;
}

#offer-included-list {
  padding-left: 20px;
}

#product-status {
  color: red;
}

#product-content-container {
  height: 100%;
  min-width: 100%; /* IE spacing fix */
  margin-top: 3%;
  margin-bottom: 60px;
}
#product-image {
  position: -webkit-sticky;
  position: sticky;
  top: 50px;
  height: 50%;
  padding-left: 5%;
}

.info-wrapper {
  max-width: 90%;
  margin: 0 auto;
}

#product-info {
  position: relative;
  max-width: unset;
}

#product-body-specs-header {
  font-weight: 500;
  font-size: 22px;
  line-height: 20px;
  color: #2e3438;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
}

#product-name {
  font-weight: bold;
  font-size: 32px;
  line-height: 44px;
  color: #2e3438;
}

#offer-list-price {
  -webkit-text-decoration-line: line-through; /* Safari */
  -webkit-text-decoration-color: #cd1426;
  -webkit-text-decoration-thickness: 2px;
  text-decoration-line: line-through;
  text-decoration-color: #cd1426;
  text-decoration-thickness: 2px;
  font-size: 16px;
  line-height: 28px;
  color: #00263e;
}

.section-margin-1 {
  margin-top: 24px;
}

.section-margin-2 {
  margin-top: 32px;
}

.offer-bullet {
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  color: #474f54;
  margin-top: 2%;
}

@media only screen and (min-width: 1000px) {
  #product-image {
    max-width: 49%; /* Safari display fix */
    margin-right: 90px;
  }

  #product-content-container {
    max-width: 1350px;
    min-width: unset;
    margin: 0 auto;
    padding-top: 30px;
  }

  #product-info {
    max-width: 49%; /* Safari display fix */
    padding-left: 12px;
  }
}
@media only screen and (max-width: 1000px){
  #product-image {
    position: relative; 
  }
}
.roboto {
  font-family: "Roboto", Helvetica, sans-serif !important;
}
.small-fleet-list-price {
  -webkit-text-decoration-line: line-through; /* Safari */
  -webkit-text-decoration-color: #cd1426;
  -webkit-text-decoration-thickness: 2px;
  text-decoration-line: line-through;
  text-decoration-color: #cd1426;
  text-decoration-thickness: 2px;
  position: relative;
  font-size: 32px;
  line-height: 24px;
  color: #0184fb;
  font-weight: bold;
}
.small-fleet-sale-price {
  font-size: 32px;
  line-height: 24px;
  color: #0184fb;
  font-weight: bold;
}
.accordion-wrapper.easeInOutCirc {
  transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);
}
.accordion-content{
  font-weight:400;
  color: blue;
  margin-top: 20px;
}

.description {
  font-size: 20px;
  line-height: 26px;
  color: #474f54;
}

.quantity-section {
  margin-top: 0px;
  margin-bottom: 50px;
}

.quantity-section ::v-deep .tile {
  margin-top: 40px;
}

.specs-section {
  margin-top: 50px;
  margin-bottom: 50px;
  padding-top: 50px;
  padding-bottom: 50px;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
}

.product-body-section-headers {
  font-weight: 500;
  font-size: 22px;
  line-height: 20px;
  color: #2e3438;
  margin-bottom: 10px;
}

.product-body-section-headers.cable-selection {
  max-width: 90%;
  margin: 40px auto;
  margin-top: 60px;
}

#accessories-carousel-header{
    font-weight: 500;
    font-size: 28px;
    line-height: 28px;
    color: #2e3438;
    margin-bottom: 15px;
}

#accessories-carousel-messaging{
    position: relative;
    max-width: 1350px;
    margin: 0 auto;
    padding: 22px;
    margin-bottom: 10px;
    margin-top: 40px;

    @media only screen and (max-width: 1360px) {
      text-align: center;
    }
}

.add-to-bag {
  width: 250px;
  float: right;
}

.grayed {
  opacity: 0.35;
  position: relative;
  overflow: hidden;
  cursor: not-allowed;
  z-index: 100;
  pointer-events: none;
}

.product-page__rows {
  margin-top: 180px;

  @media only screen and (min-width: 476px) {
    margin-top: 134px;
  }

  @media only screen and (min-width: 1000px) {
    margin-top: 100px;
  }
  
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

/* Add animation (fade in the popup) */
@-webkit-keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
  from {
    visibility: hidden;
  }
  to {
    visibility: visible;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
  from {
    visibility: hidden;
  }
  to {
    visibility: visible;
  }
}
</style>
